import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
  LINKED_ENTITY_CHIP_ICON_SIZE,
  LINKED_ENTITY_CHIP_VERTICAL_PADDING,
  PART_ANNOTATION_COLOR,
  PART_LINK_ARROW_COLOR,
  LINKED_ENTITY_CHIP_HEIGHT,
  SINGLE_ANNOTATION_RADIUS,
} from 'molbio/maps/BiointelligentLinksConstants';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface BiointelligentLinkChipProps {
  linkName: string;
  linkIcon: BenchlingIconName;
  chipMaxWidth: number;
}

/**
 * Chip-like component for use inside of the complex nested SVGs in the sequence map.
 *
 * It's a bit less awkward to use a foreignObject for the entire chip and style it with
 * CSS here - in part because we would need a foreignObject anyways for the icon, in
 * part because it's a pain to center things programmatically with svg
 */
export default function BiointelligentLinkChip({
  linkName,
  linkIcon,
  chipMaxWidth,
}: BiointelligentLinkChipProps): JSX.Element {
  return (
    <foreignObject
      height={LINKED_ENTITY_CHIP_HEIGHT}
      width={chipMaxWidth}
      x={LINKED_ENTITY_CHIP_HORIZONTAL_PADDING}
      y={LINKED_ENTITY_CHIP_VERTICAL_PADDING * 2}
    >
      <span
        className={css(styles.chip)}
        style={{maxWidth: chipMaxWidth - LINKED_ENTITY_CHIP_HORIZONTAL_PADDING * 2}}
      >
        <BenchlingIcon icon={linkIcon} className={css(styles.icon)} />
        <span
          className={css(styles.chipText)}
          style={{maxWidth: chipMaxWidth - LINKED_ENTITY_CHIP_HORIZONTAL_PADDING * 2}}
        >
          {linkName}
        </span>
      </span>
    </foreignObject>
  );
}

const styles = StyleSheet.create({
  chip: {
    backgroundColor: PART_LINK_ARROW_COLOR,
    color: PART_ANNOTATION_COLOR,
    borderRadius: SINGLE_ANNOTATION_RADIUS,
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    padding: `${LINKED_ENTITY_CHIP_VERTICAL_PADDING}px ${LINKED_ENTITY_CHIP_HORIZONTAL_PADDING}px`,
    cursor: 'pointer',
  },
  chipText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  icon: {
    color: PART_ANNOTATION_COLOR,
    height: LINKED_ENTITY_CHIP_ICON_SIZE,
    width: LINKED_ENTITY_CHIP_ICON_SIZE,
    marginRight: LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
  },
});
